// Sidebar met alle 7 clusters + progress-indicator per cluster.
// Status-kleur:
//   groen   = compleet (alle required ingevuld + geen open risico)
//   geel    = in behandeling (≥1 item ingevuld, nog niet compleet)
//   grijs   = niet gestart
//   rood    = open risico (overschrijft andere statussen)
//
// Daarnaast per cluster een inklapbare "openstaande acties"-lijst met
// deeplink: klik → navigate naar cluster met ?focus=<itemId>, waarna
// ClusterRunnerPage scrollt en highlight.

import React, { useState } from 'react';
import {
    Box,
    Chip,
    Collapse,
    Divider,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ErrorIcon from '@mui/icons-material/Error';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function statusFor(cluster, p, risks) {
    const hasRisk = risks?.some(r => r.cluster === cluster.id && r.status === 'open' && r.relevant !== false);
    if (hasRisk) { return 'risk'; }
    if (!p || p.total === 0) { return 'notStarted'; }
    if (p.complete) { return 'complete'; }
    if (p.filled === 0) { return 'notStarted'; }
    return 'inProgress';
}

const STATUS_META = {
    complete: { color: 'success.main', Icon: CheckCircleIcon, key: 'cluster.status.complete' },
    inProgress: { color: 'warning.main', Icon: PlayCircleOutlineIcon, key: 'cluster.status.inProgress' },
    notStarted: { color: 'text.disabled', Icon: RadioButtonUncheckedIcon, key: 'cluster.status.notStarted' },
    risk: { color: 'error.main', Icon: ErrorIcon, key: 'cluster.status.hasRisk' },
};

export default function ClusterSidebar({ clusters, progress, risks = [], missingByCluster = {} }) {
    const navigate = useNavigate();
    const { clusterId: activeClusterId, projectId } = useParams();
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState({});

    const openRiskCount = risks.filter(r => r.status === 'open' && r.relevant !== false).length;

    const goTo = (clusterId, itemId) => {
        const url = `/projects/${projectId}/wizard/${clusterId}${itemId ? `?focus=${encodeURIComponent(itemId)}` : ''}`;
        navigate(url);
    };

    const toggle = (ev, clusterId) => {
        ev.stopPropagation();
        setExpanded(prev => ({ ...prev, [clusterId]: !prev[clusterId] }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="overline" color="text.secondary">
                    {t('wizard.cluster')}
                </Typography>
            </Box>
            <List dense sx={{ flex: 1, overflow: 'auto' }}>
                {clusters.map(cluster => {
                    const p = progress?.[cluster.id];
                    const status = statusFor(cluster, p, risks);
                    const meta = STATUS_META[status];
                    const Icon = meta.Icon;
                    const pct = p?.total ? Math.round((p.filled / p.total) * 100) : 0;
                    const isActive = activeClusterId === cluster.id;
                    const missing = missingByCluster[cluster.id] || [];
                    const isExpanded = !!expanded[cluster.id];
                    return (
                        <React.Fragment key={cluster.id}>
                            <ListItem
                                disablePadding
                                secondaryAction={missing.length > 0 ? (
                                    <IconButton
                                        edge="end"
                                        size="small"
                                        onClick={(ev) => toggle(ev, cluster.id)}
                                        sx={{ color: 'warning.dark', mr: 0.5 }}
                                        aria-label={`${missing.length} openstaande items`}
                                    >
                                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                ) : null}
                            >
                                <ListItemButton
                                    selected={isActive}
                                    onClick={() => goTo(cluster.id)}
                                    sx={{ py: 1.5, pr: missing.length > 0 ? 6 : 2 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 36, color: meta.color }}>
                                        <Icon />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={cluster.title}
                                        primaryTypographyProps={{
                                            fontWeight: isActive ? 600 : 500,
                                            fontSize: 14,
                                        }}
                                        secondary={
                                            <Stack component="span" sx={{ mt: 0.5 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={pct}
                                                    sx={{ height: 4, borderRadius: 2 }}
                                                    color={
                                                        status === 'complete' ? 'success'
                                                            : status === 'risk' ? 'error'
                                                                : status === 'inProgress' ? 'warning'
                                                                    : 'inherit'
                                                    }
                                                />
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ mt: 0.3, display: 'block' }}>
                                                    {p?.filled || 0} / {p?.total || 0}
                                                    {missing.length > 0 && (
                                                        <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600, ml: 1 }}>
                                                            · {missing.length} open
                                                        </Box>
                                                    )}
                                                </Typography>
                                            </Stack>
                                        }
                                        secondaryTypographyProps={{ component: 'div' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                            <Collapse in={isExpanded && missing.length > 0}>
                                <List dense disablePadding sx={{ bgcolor: 'rgba(230,142,45,0.05)', pl: 5, pr: 1 }}>
                                    {missing.map(m => (
                                        <ListItem key={m.itemId} disablePadding>
                                            <ListItemButton
                                                onClick={() => goTo(cluster.id, m.itemId)}
                                                sx={{ py: 0.5 }}
                                            >
                                                <ArrowForwardIcon sx={{ fontSize: 12, color: 'warning.dark', mr: 1 }} />
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', minWidth: 36 }}>
                                                                {m.itemId}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'text.primary' }}>
                                                                {m.label}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            </Collapse>
                        </React.Fragment>
                    );
                })}
            </List>

            <Divider />
            <List dense>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => navigate(`/projects/${projectId}/risks`)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <AssignmentLateIcon color={openRiskCount > 0 ? 'error' : 'disabled'} />
                        </ListItemIcon>
                        <ListItemText primary={t('wizard.riskRegister')} />
                        {openRiskCount > 0 && (
                            <Chip size="small" color="error" label={openRiskCount} />
                        )}
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => navigate(`/projects/${projectId}/report`)}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            <AssignmentTurnedInIcon />
                        </ListItemIcon>
                        <ListItemText primary={t('wizard.report')} />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );
}
